// Sending people back to where they were after they sign in or create an
// account.
//
// The destination travels as a `next` query parameter
// (/signin?next=/faq-platform-faq/overview) rather than in router state, so it
// survives a page reload and the full-page navigation an authored HTML link
// makes. Anyone can put anything in a URL, so it is validated before use.

const HOME = '/';

// Pages that are themselves part of a sign-in or sign-up flow. They are never a
// destination (that would loop), and when a flow moves between them the
// original destination is carried along rather than replaced.
const AUTH_PATHS = new Set(['/signin', '/signup', '/accept-invite', '/reset-password']);

// Accept only a path on this site that is not an auth page. Anything else —
// another site, a protocol-relative "//evil.example", "javascript:", a
// malformed value — falls back to home. This is what stops the sign-in page
// being usable as an open redirect (sign in, then land on a phishing page).
export function safeReturnPath(raw) {
  if (typeof raw !== 'string' || !raw.startsWith('/')) return HOME;
  let url;
  try {
    url = new URL(raw, window.location.origin);
  } catch {
    return HOME;
  }
  if (url.origin !== window.location.origin) return HOME;
  if (AUTH_PATHS.has(url.pathname)) return HOME;
  return url.pathname + url.search + url.hash;
}

// Where a sign-in or sign-up started from `location` should return to.
// - From an ordinary page: that page (path, query and hash).
// - From inside a flow (e.g. Sign In → "Create an account"): the destination
//   that flow was already carrying, so the person still ends up back on the
//   page that first sent them, not on the sign-in page.
// Also used by the auth pages themselves to decide where to go on completion.
export function returnPathFor(location) {
  if (AUTH_PATHS.has(location.pathname)) {
    return safeReturnPath(new URLSearchParams(location.search).get('next'));
  }
  return safeReturnPath(location.pathname + location.search + location.hash);
}

// The URL of an auth page (`/signin` or `/signup`) that will send the person
// back afterwards. Home needs no parameter — it is the default.
export function authUrl(authPath, location) {
  const next = returnPathFor(location);
  return next === HOME ? authPath : `${authPath}?next=${encodeURIComponent(next)}`;
}
